import type {
  SchemaVariant,
  TemplateSave,
  TreeNode,
} from '@morph-mapper/types';
import { getValues } from '@morph-mapper/utils';
import { useStore } from '@/store';
import type { StoredSuggestion } from '@/store';
import { parseZodSchema } from '@/utils';
import { z } from 'zod';
import { usePlugins } from '@morph-mapper/plugins';

export const useLoadState = () => {
  /* Global state */
  const [
    setName,
    setSchemaId,
    setDomain,
    setType,
    setVariant,
    setPreconfiguration,
    setConfigSnapshot,
    setDiscardFields,
    setLastTextContentHash,
    setEntries,
    setGraphs,
    loadSuggestions,
    resetAiGuards,
  ] = useStore(({ config: c, entries: e, graphs: g, ai }) => [
    c.setName,
    c.setSchemaId,
    c.setDomain,
    c.setType,
    c.setVariant,
    c.setPreconfiguration,
    c.setConfigSnapshot,
    c.setDiscardFields,
    c.setLastTextContentHash,
    e.setEntries,
    g.setGraphs,
    ai.loadSuggestions,
    ai.resetForNewFile,
  ]);

  const { setContext } = usePlugins();

  /**
   * Handlers
   */
  const replaceNull = (obj: any) => {
    for (const k in obj) {
      if (typeof obj[k] == 'object' && obj[k] !== null) {
        replaceNull(obj[k]);
      } else if (obj[k] === null) {
        obj[k] = undefined;
      }
    }

    return obj;
  };

  // TODO: this is a pre-hydrated treenode, update typing
  const parseEntries = (entries: Record<string, TreeNode>) => {
    return replaceNull(
      getValues(entries).reduce(
        (acc, entry: TreeNode) => {
          // TODO: why can this happen? Fix: ZodUnknown for new environment variables
          const validation = entry.validation
            ? parseZodSchema(entry.validation)
            : z.any();

          acc[entry.id] = { ...entry, validation };

          return acc;
        },
        {} as Record<string, TreeNode>,
      ),
    );
  };

  const load = (template: Omit<TemplateSave, 'id'>) => {
    const {
      entries,
      graphs,
      name,
      domain,
      type,
      variant,
      schemaId,
      config,
      suggestions,
    } = template;
    const {
      updateDiscardIds,
      preconfiguration,
      configSnapshot,
      lastTextContentHash,
    } = config;

    setName(name);
    setSchemaId(schemaId);
    setDomain(domain);
    setType(type);
    setVariant(variant);
    setEntries(parseEntries(entries));
    setGraphs(graphs);
    setPreconfiguration(preconfiguration);
    setConfigSnapshot(configSnapshot);
    setDiscardFields(updateDiscardIds);
    setLastTextContentHash(lastTextContentHash);
    loadSuggestions((suggestions ?? {}) as Record<string, StoredSuggestion[]>);
    resetAiGuards(null);

    // Add context to plugins
    setContext(domain, variant as SchemaVariant);
  };

  return { load };
};
